import {
  EmailOutlined,
  GamepadOutlined,
  Groups2Outlined,
  InsertChartOutlined,
  PersonPinOutlined,
  SettingsOutlined,
  SpeedOutlined,
  TableChartOutlined,
} from "@mui/icons-material";
import {
  Badge,
  Box,
  type ListItemButtonProps,
  ListItemIcon,
  ListItemText,
  ListItemButton as MuiListItemButton,
  type SvgIconProps,
} from "@mui/material";
import type { LinkProps } from "@tanstack/react-router";
import { createLink, useLocation } from "@tanstack/react-router";
import { forwardRef, type JSX } from "react";
import { useTrovePlan } from "@/hooks/trove-plan";
import { PricingFeature, useGetToNotify } from "@/models/backend";

export function SideNav({ onNav }: { onNav: () => void }): JSX.Element {
  const { pathname } = useLocation();
  const { features, main_account } = useTrovePlan();
  const { data: toNotify } = useGetToNotify();
  const activityBadgeCount = toNotify ? toNotify.outbox_due + toNotify.new_incoming : 0;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        gap: 0.5,
        padding: 1,
        marginTop: "64px",
        width: "100%",
      }}
    >
      {pathname.startsWith("/admin") ? (
        features.includes(PricingFeature.multi_account) && (
          <Box
            component="nav"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
            }}
          >
            <NavItem
              pathname={pathname}
              title="Organisations"
              linkOptions={{ to: "/admin/organizations" }}
              icon={Groups2Outlined}
              onSelect={() => onNav()}
            />
            <NavItem
              pathname={pathname}
              title="Workflows"
              linkOptions={{ to: "/admin/workflows" }}
              icon={GamepadOutlined}
              onSelect={() => onNav()}
            />
            <NavItem
              pathname={pathname}
              title="Settings"
              linkOptions={{ to: "/admin/settings" }}
              icon={SettingsOutlined}
              onSelect={() => onNav()}
            />
          </Box>
        )
      ) : (
        <Box
          component="nav"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
          }}
        >
          <NavItem
            pathname={pathname}
            title="Dashboard"
            linkOptions={{ to: "/dashboard" }}
            icon={SpeedOutlined}
            onSelect={() => onNav()}
          />
          <NavItem
            pathname={pathname}
            title="Activity"
            linkOptions={{ to: "/events" }}
            icon={EmailOutlined}
            badge={activityBadgeCount > 0 ? activityBadgeCount : undefined}
            onSelect={() => onNav()}
          />
          <NavItem
            pathname={pathname}
            title="Customers"
            linkOptions={{ to: "/customers" }}
            icon={PersonPinOutlined}
            onSelect={() => onNav()}
          />
          <NavItem
            pathname={pathname}
            title="Reports"
            linkOptions={{ to: "/reports" }}
            icon={InsertChartOutlined}
            onSelect={() => onNav()}
          />
          <NavItem
            pathname={pathname}
            title="Board"
            linkOptions={{ to: "/kanban" }}
            icon={TableChartOutlined}
            onSelect={() => onNav()}
          />
          <NavItem
            pathname={pathname}
            title="Workflows"
            linkOptions={{ to: "/workflows" }}
            icon={GamepadOutlined}
            onSelect={() => onNav()}
          />
          <NavItem
            pathname={pathname}
            title="Settings"
            linkOptions={{ to: "/settings" }}
            icon={SettingsOutlined}
            onSelect={() => onNav()}
          />
        </Box>
      )}
      {features.includes(PricingFeature.multi_account) && (
        <Box
          component="nav"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            marginTop: "auto",
          }}
        >
          {pathname.startsWith("/admin") ? (
            <NavItem
              title="Dashboard"
              linkOptions={{ to: "/dashboard" }}
              icon={SpeedOutlined}
              onSelect={() => onNav()}
            />
          ) : (
            <NavItem
              title="Admin portal"
              linkOptions={{
                to: "/admin/organizations",
                ...(main_account ? { search: { organizationId: main_account } } : {}),
              }}
              icon={Groups2Outlined}
              onSelect={() => onNav()}
            />
          )}
        </Box>
      )}
    </Box>
  );
}

const MUIListItemButton = forwardRef<HTMLAnchorElement, ListItemButtonProps<"a">>((props, ref) => (
  <MuiListItemButton ref={ref} component="a" {...props} />
));

const ListItemButton = createLink(MUIListItemButton);

export function NavItem({
  linkOptions,
  icon,
  pathname,
  title,
  badge,
  onSelect,
}: {
  linkOptions: LinkProps;
  icon: React.ComponentType<SvgIconProps>;
  pathname?: string;
  title: string;
  badge?: number;
  onSelect?: () => void;
}): React.JSX.Element {
  const active =
    typeof linkOptions.to === "string" &&
    pathname !== undefined &&
    pathname.startsWith(linkOptions.to);
  const Icon = icon;

  return (
    <ListItemButton selected={active} {...linkOptions} onClick={onSelect}>
      {Icon && (
        <ListItemIcon sx={{ minWidth: "30px" }}>
          <Badge badgeContent={badge} color="primary">
            <Icon fontSize="small" />
          </Badge>
        </ListItemIcon>
      )}
      <ListItemText
        slotProps={{
          primary: { variant: "body2", sx: { fontWeight: active ? "semiBold" : undefined } },
        }}
      >
        {title}
      </ListItemText>
    </ListItemButton>
  );
}
